import React, { useEffect, useState } from 'react';
import { Modal, Input, Button, Tag, Tooltip, message, Spin } from 'antd';
import { EditOutlined, DeleteOutlined, SaveOutlined, QuestionCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { isValidGlossaryTerm } from '../utils/glossaryValidation';

interface DemoNeverTranslateModalProps {
  isVisible: boolean;
  onClose: () => void;
  currentLanguagePair: string;
  neverTranslateWords: { [pair: string]: string[] };
  setNeverTranslateWords: React.Dispatch<React.SetStateAction<{ [pair: string]: string[] }>>;
}

const DemoNeverTranslateModal: React.FC<DemoNeverTranslateModalProps> = ({
  isVisible,
  onClose,
  currentLanguagePair,
  neverTranslateWords,
  setNeverTranslateWords,
}) => {
  const [newWord, setNewWord] = useState('');
  const [editingWord, setEditingWord] = useState<string | null>(null);
  const [editedWord, setEditedWord] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isVisible) {
      setNewWord('');
      setEditingWord(null);
      setEditedWord('');
    }
  }, [isVisible]);

  const isValidInput = isValidGlossaryTerm;

  // Simulate async "save" for UX parity (no backend in demo)
  const withSimulatedSave = async (fn: () => void) => {
    setIsLoading(true);
    try {
      await new Promise((res) => setTimeout(res, 350));
      fn();
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddWord = async () => {
    if (!currentLanguagePair) {
      message.error('No language pair selected.');
      return;
    }
    const value = newWord.trim();
    if (!value) {
      message.error('Please enter a word or phrase.');
      return;
    }
    if (!isValidInput(value)) {
      message.error('Please enter a valid word or phrase (letters, numbers, spaces, hyphens, apostrophes).');
      return;
    }
    await withSimulatedSave(() => {
      const updated = [...(neverTranslateWords[currentLanguagePair] || []), value];
      setNeverTranslateWords((prev) => ({ ...prev, [currentLanguagePair]: updated }));
      setNewWord('');
      message.success('Added.');
    });
  };

  const handleEditWord = (oldWord: string) => {
    setEditingWord(oldWord);
    setEditedWord(oldWord);
  };

  const handleSaveEdit = async () => {
    if (!currentLanguagePair || !editingWord) return;
    const value = editedWord.trim();
    if (!value) {
      message.error('Please enter a word or phrase.');
      return;
    }
    if (!isValidInput(value)) {
      message.error('Please enter a valid word or phrase (letters, numbers, spaces, hyphens, apostrophes).');
      return;
    }
    await withSimulatedSave(() => {
      const updated = (neverTranslateWords[currentLanguagePair] || []).map((w) => (w === editingWord ? value : w));
      setNeverTranslateWords((prev) => ({ ...prev, [currentLanguagePair]: updated }));
      setEditingWord(null);
      message.success('Saved.');
    });
  };

  const handleRemoveWord = async (word: string) => {
    if (!currentLanguagePair) return;
    await withSimulatedSave(() => {
      const updated = (neverTranslateWords[currentLanguagePair] || []).filter((w) => w !== word);
      setNeverTranslateWords((prev) => ({ ...prev, [currentLanguagePair]: updated }));
      message.success('Removed.');
    });
  };

  const handleClose = () => {
    setNewWord('');
    setEditingWord(null);
    setEditedWord('');
    onClose();
  };

  return (
    <Modal
      title={
        <span>
          Text to Never Translate for {currentLanguagePair || '<none>'}
          <Tooltip title="The AI will be instructed to never translate the text entered here">
            <QuestionCircleOutlined style={{ marginLeft: 8, cursor: 'pointer', color: '#1677ff' }} />
          </Tooltip>
        </span>
      }
      visible={isVisible}
      onCancel={handleClose}
      footer={[<Button key="close" onClick={handleClose}>Close</Button>]}
      width={700}
    >
      <Spin spinning={isLoading}>
        <div style={{ background: '#f7f7f7', padding: 15, borderRadius: 6, marginBottom: 15 }}>
          <div style={{ marginBottom: 15 }}>
            <label style={{ display: 'block', marginBottom: 5 }}><strong>Word or Phrase to Never Translate:</strong></label>
            <Input
              value={newWord}
              onChange={(e) => setNewWord(e.target.value)}
              placeholder="Enter word or phrase to never translate"
            />
          </div>
          <Button type="primary" onClick={handleAddWord} icon={<PlusOutlined />}>
            Add Word/Phrase
          </Button>
        </div>

        <div style={{ background: '#f0f7ff', padding: 15, borderRadius: 6 }}>
          <div style={{ marginBottom: 12 }}>
            <strong>Current Text to Never Translate:</strong>
            <Tooltip title="The AI will never translate these words or phrases">
              <QuestionCircleOutlined style={{ marginLeft: 5, color: '#1677ff' }} />
            </Tooltip>
          </div>

          <div style={{ maxHeight: 300, overflowY: 'auto', padding: '5px 0' }}>
            {currentLanguagePair && (neverTranslateWords[currentLanguagePair] ?? []).length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', margin: '0 -5px' }}>
                {(neverTranslateWords[currentLanguagePair] ?? []).map((word, index) => (
                  <div key={index} style={{ width: editingWord === word ? '100%' : '50%', padding: '0 5px', boxSizing: 'border-box', marginBottom: 10 }}>
                    <Tag style={{ padding: '6px 8px', fontSize: 14, display: 'flex', alignItems: 'center', width: '100%' }}>
                      {editingWord === word ? (
                        <>
                          <Input value={editedWord} onChange={(e) => setEditedWord(e.target.value)} style={{ width: 220, marginRight: 8 }} />
                          <Tooltip title="Save">
                            <SaveOutlined onClick={handleSaveEdit} style={{ marginLeft: 5, cursor: 'pointer', color: '#1677ff' }} />
                          </Tooltip>
                        </>
                      ) : (
                        <>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{word}</span>
                          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexShrink: 0 }}>
                            <Tooltip title="Edit">
                              <EditOutlined onClick={() => handleEditWord(word)} style={{ cursor: 'pointer', color: '#1677ff' }} />
                            </Tooltip>
                            <Tooltip title="Delete">
                              <DeleteOutlined onClick={() => handleRemoveWord(word)} style={{ cursor: 'pointer', color: '#ff4d4f' }} />
                            </Tooltip>
                          </div>
                        </>
                      )}
                    </Tag>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: '#999', padding: '10px 0' }}>No words or phrases added yet. Add your first word or phrase above.</div>
            )}
          </div>
        </div>
      </Spin>
    </Modal>
  );
};

export default DemoNeverTranslateModal;
